import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserModel } from '../models/user-model';
import {
  KvkkApprovalModel,
  UserDetailModel,
} from '../models/user-detail-model';
import { DeviceService } from './devices-services';
import { CMSDynamicService } from './cms-service';
import { JwtHelperService } from '@auth0/angular-jwt';

/**
 * UnifiedAuthService
 * - admin + student/lecturer login tek serviste
 * - CMSDynamicService ile çalışır
 * - identityCheck sadece admin logininden sonra çağrılır
 */
@Injectable({ providedIn: 'root' })
export class UnifiedAuthService {
  private _userSubject = new BehaviorSubject<UserModel | null>(null);
  public user$ = this._userSubject.asObservable();

  private _isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this._isLoggedIn$.asObservable();

  private _isDeviceRegistered$ = new BehaviorSubject<boolean>(false);
  public isDeviceRegistered$ = this._isDeviceRegistered$.asObservable();
  private _token: string | null = null;

  // flags
  public isSifreKontrol = false;
  public isEmailKontrol = false;
  public hazirlikMi = false;
  public ogrenciMi = false;
  public akademikPersonelMi = true;

  // storage keys
  private storageKeyUser = 'userList';
  private storageKeyToken = 'accessToken'; // TEK TOKEN KEY
  private storageKeyIsLogged = 'isLoggedIn';

  constructor(
    private cms: CMSDynamicService,
    private deviceService: DeviceService,
    private jwtHelper: JwtHelperService,
  ) {
    this.loadUserFromStorage();
  }

  // --- getters
  get token() {

    if (this._token) return this._token;
    const t = localStorage.getItem(this.storageKeyToken);


    if (t) this._token = t;
    return t;
  }

  get userValue(): UserModel | null {
    return this._userSubject.value;
  }

  // --- persistence helpers
  private saveUserToStorage() {
    try {
      if (this.userValue) {
        localStorage.setItem(
          this.storageKeyUser,
          JSON.stringify(this.userValue),
        );
      } else {
        localStorage.removeItem(this.storageKeyUser);
      }

      if (this._token) localStorage.setItem(this.storageKeyToken, this._token);
      else localStorage.removeItem(this.storageKeyToken);

      localStorage.setItem(
        this.storageKeyIsLogged,
        JSON.stringify(this._isLoggedIn$.value),
      );
    } catch (err) {
      console.error('Error saving user to storage', err);
    }
  }

  private loadUserFromStorage() {
    try {
      const userJson = localStorage.getItem(this.storageKeyUser);
      const token = localStorage.getItem(this.storageKeyToken);

      if (userJson && token) {
        const parsed: UserModel = JSON.parse(userJson);
        this._userSubject.next(parsed);
        this._token = token;
        this._isLoggedIn$.next(true);
        console.log('✅ User loaded ');
      } else {
        this._userSubject.next(null);
        this._isLoggedIn$.next(false);
      }
    } catch (err) {
      console.error('Error loading user ', err);
      this._userSubject.next(null);
      this._isLoggedIn$.next(false);
    }
  }

  // --- unified login entry
  async login(
    usernameOrStudentNo: string,
    password: string,
    isAdmin: boolean,
  ): Promise<void> {

    this.storageClear();
    if (isAdmin) {
      return this.loginAdmin(usernameOrStudentNo, password);
    } else {
      return this.loginUser(usernameOrStudentNo, password);
    }
  }

  // --- admin login
  private async loginAdmin(email: string, password: string): Promise<void> {
    const controller = 'Auth';
    const action = 'Login';
    const message = 'Yönetici girişi yapılıyor';

    const body = { email, password };

    try {
      const response = await this.cms.post(
        controller,
        action,
        message,
        body,
        false,
      );

      if (response && response['success'] !== false) {
        const userModel: UserModel = this.mapResponseToUserModel(
          response['Login'] ?? response,
          response,
        );

      console.table(userModel);

        this._userSubject.next(userModel);
        this._token = response['accessToken'] ?? response['token'] ?? '';
        localStorage.setItem(this.storageKeyToken, this._token ?? '');

        this._isLoggedIn$.next(true);
        if (response['roles'])
          localStorage.setItem('Roles', JSON.stringify(response['roles']));

        this.identityCheck();
        const deviceId = response['DeviceId'] ?? response['deviceId'] ?? '';

        const isRegistered = deviceId
          ? await this.deviceService.checkDeviceRegistered(deviceId)
          : false;

        this.setDeviceRegistered(isRegistered);
        this.saveUserToStorage();
        return;
      } else {
        throw new Error(response?.message ?? 'Admin login failed');
      }
    } catch (err) {
      this.resetAuthState();
      console.error('Admin login failed', err);
      throw err;
    }
  }
  setDeviceRegistered(val: boolean) {
    this._isDeviceRegistered$.next(val);
  }

storageClear() {
  const keysToRemove = [
    "attendance_cache",
  ];

  // tekil silinenler
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
  });

  // prefix ile silinenler
  Object.keys(localStorage).forEach((key) => {
    if (
      key.startsWith("courseStudent_") ||
      key.startsWith("dersler_") ||
      key.startsWith("erslerLastUpdate_")
    ) {
      localStorage.removeItem(key);
    }
  });
}

  private async loginUser(
  studentNumber: string,
  password: string,
): Promise<void> {
  const controller = 'Kapi';
  const action = 'LoginAndFetchAll';
  const message = 'Login işlemi yapılıyor';

  const body = { kullaniciAd: studentNumber, sifre: password,web:true };

  try {
    const response = await this.cms.LoginPost(
      controller,
      action,
      message,
      body,
      false,
    );
    var akademikMi = false; // loginUser çağrısı zaten akademikMi bilgisini içeriyor, bu yüzden parametre olarak geçmeye gerek yok




    if (response && response['Login'] && response['Login']['data']) {
      const res = response['Login'];
      const res2 = response;
      const userModel: UserModel = this.mapResponseToUserModel(res, res2);

      this._userSubject.next(userModel);
      this._token = response['accessToken'] ?? '';
      localStorage.setItem(this.storageKeyToken, this._token ?? '');
      this._isLoggedIn$.next(true);
      var no = userModel.data[0]?.userNo

  akademikMi = userModel.data[0]?.akademikPersonelMi ?? false;


      const deviceId = response['DeviceId'] ?? response['deviceId'] ?? '';

      const isRegistered = deviceId
        ? await this.deviceService.checkDeviceRegistered(deviceId)
        : false;

      this.setDeviceRegistered(isRegistered);

      const deviceChangedCount =
        response['DeviceChangedCount'] ?? response['deviceChangedCount'] ?? 0;
      const deviceTableId =
        response['DeviceTableId'] ?? response['deviceTableId'] ?? 0;

      if (userModel.data && userModel.data.length > 0) {
        const last = userModel.data[userModel.data.length - 1];
        last.deviceId = deviceId ?? last.deviceId ?? '';
        last.deviceChangedCount =
          deviceChangedCount ?? last.deviceChangedCount ?? 0;
        last.deviceTableId = deviceTableId ?? last.deviceTableId ?? 0;
      }

      // role flags
      if (akademikMi) {
        this.akademikPersonelMi = true;
        this.ogrenciMi = false;
        this.hazirlikMi = false;


        await localStorage.setItem(
          'akademikPersonelMi',
          JSON.stringify(true),
        );
        await localStorage.setItem('ogrenciMi', JSON.stringify(false));
        await localStorage.setItem('hazirlikMi', JSON.stringify(false));
      } else {
        this.akademikPersonelMi = false;
        this.ogrenciMi = true;
        this.hazirlikMi = false;

        const firstUser = response['Login']['data'];
        const firstBirim = firstUser?.birimler?.[0];



        const programId = firstBirim?.birimId;
        const ogrenciNumarasi = firstBirim?.ogrenciNumarasi;

        if (firstUser?.ogrenciMi) {
          localStorage.setItem('programId', programId ?? '0');
          localStorage.setItem('ogrenciNumarasi', ogrenciNumarasi ?? '0');
        }



        await localStorage.setItem(
          'akademikPersonelMi',
          JSON.stringify(false),
        );
        await localStorage.setItem('ogrenciMi', JSON.stringify(true));
        await localStorage.setItem('hazirlikMi', JSON.stringify(false));
      }

      this.isEmailKontrol = userModel.data[0]?.emailKontrolOnay ?? false;
      this.isSifreKontrol = userModel.data[0]?.sifreKontrolOnay ?? false;

      // syllabus & dersler
      if (response['Syllabus'] && Array.isArray(response['Syllabus'])) {
        userModel.data[0].syllabus = response['Syllabus'].map((s: any) => s);
        await localStorage.setItem(
          'dersler',
          JSON.stringify(userModel.data[0].syllabus),
        );
      }

      if (response['HocaDers'] && Array.isArray(response['HocaDers'])) {
        userModel.data[0].dersler = response['HocaDers'].map((d: any) => d);
        await localStorage.setItem(
          'hocaDersler',
          JSON.stringify(userModel.data[0].dersler),
        );
      }

      this.saveUserToStorage();
      return;
    } else {
      throw new Error('Login response is invalid');
    }
  } catch (err) {
    this.resetAuthState();
    console.error('Login failed', err);
    throw err;
  }
}


  // --- identity check
  identityCheck() {
    const token: string = localStorage.getItem(this.storageKeyToken) || '';

    let expired: boolean;
    try {
      expired = this.jwtHelper.isTokenExpired(token);
    } catch {
      expired = true;
    }

    const _isAuthenticated = token != null && !expired;

    const _accessToken = localStorage.getItem(this.storageKeyToken) || '';

    return { _isAuthenticated, _accessToken };
  }

  // --- logout
  async logout(): Promise<void> {
    try {
      localStorage.removeItem(this.storageKeyUser);
      localStorage.removeItem(this.storageKeyToken);
      localStorage.removeItem(this.storageKeyIsLogged);
      this._token = null;
      this._isLoggedIn$.next(false);
      this._userSubject.next(null);
      this.isSifreKontrol = false;
      this.isEmailKontrol = false;
      this.ogrenciMi = false;
      this.hazirlikMi = false;
      this.akademikPersonelMi = false;
      this.setDeviceRegistered(false);
    } catch (err) {
      console.error('Logout error', err);
    }
  }

  // --- helpers
  private resetAuthState() {
    this._isLoggedIn$.next(false);
    this._userSubject.next(null);
    this._token = null;
    this.isSifreKontrol = false;
    this.isEmailKontrol = false;
    this.ogrenciMi = false;
    this.hazirlikMi = false;
    this.akademikPersonelMi = true;
    this.setDeviceRegistered(false);
    this.saveUserToStorage();
  }

  // --- mapping
  private mapResponseToUserModel(res: any, res2: any): UserModel {
    const dataArray: UserDetailModel[] = [];

    if (res['data']) {
      if (Array.isArray(res['data'])) {
        var a =
          res2['KvkkApprovals']?.items || res2['KvkkApprovals']?.Items || [];


        dataArray.push(
          ...(res['data'] as any[]).map((d) => this.mapToUserDetail(d, a)),
        );
      } else if (typeof res['data'] === 'object') {
        dataArray.push(
          this.mapToUserDetail(res['data'], res2['KvkkApprovals']?.Items || []),
        );
      }
    }

    return {
      data: dataArray,
      success: res['success'] ?? false,
      message: res['message'] ?? '',
      count: res['count'] ?? dataArray.length,
    } as UserModel;
  }

  private mapToKvkkapproval(d: any): KvkkApprovalModel {

    return {
      Id: d?.id ?? 0,
      StudentId: d?.studentId ?? 0,
      ConsentType: d?.consentType ?? '',
      IsAccepted: d?.isAccepted ?? false,
      PolicyVersion: d?.policyVersion ?? null,
      Source: d?.source ?? '',
      IpAddress: d?.ipAddress ?? '',
    };
  }

  updateKvkkApprovals(newApproval: any) {
    const current = this._userSubject.value;

    if (!current) return;

    const user = current.data[0];

    const existing = user.kvkkApprovals ?? [];

    // 🔥 aynı kaydı bul (id varsa id, yoksa studentId + source ile)
    const index = existing.findIndex(
      (x) =>
        (x.Id && newApproval.Id && x.Id === newApproval.Id) ||
        (x.StudentId == newApproval.StudentId &&
          x.Source === newApproval.Source),
    );

    let updatedApprovals: any[] = [];

    if (index > -1) {
      // 🔥 UPDATE
      updatedApprovals = [...existing];
      updatedApprovals[index] = {
        ...updatedApprovals[index],
        ...newApproval,
      };
    } else {
      // 🔥 INSERT
      updatedApprovals = [...existing, newApproval];
    }

    const updatedUser: UserModel = {
      ...current,
      data: current.data.map((u, i) =>
        i === 0 ? { ...u, kvkkApprovals: updatedApprovals } : u,
      ),
    };

    this._userSubject.next(updatedUser);
    this.saveUserToStorage();
  }
  private mapToUserDetail(d: any, a: any): UserDetailModel {
    const kvkkSource = a?.KvkkApprovals ?? a?.kvkkApprovals ?? [];


    return {
      photo: d['photo'] ?? '',
      tcNo: d['tcNo'] ?? '',
      userNo: d['userNo'] ?? '',
      ad: d['ad'] ?? 'Anonim',
      soyad: d['soyAd'] ?? 'Anonim',
      email: d['email'] ?? null,
      birimler: Array.isArray(d['birimler'])
        ? d['birimler'].map((x: any) => x as any)
        : [],
      sifreKontrolOnay: d['sifreKontrol'] ?? false,
      emailKontrolOnay: d['emailKontrol'] ?? false,
      dersler: Array.isArray(d['hocaDersler'])
        ? d['hocaDersler'].map((x: any) => x as any)
        : [],
      syllabus: Array.isArray(d['syllabus'])
        ? d['syllabus'].map((x: any) => x as any)
        : [],
      hazirlikMi: d['hazirlikMi'] ?? false,
      ogrenciMi: d['ogrenciMi'] ?? false,
      akademikPersonelMi: d['akademikPersonelMi'] ?? true,
      deviceId: d['deviceId'] ?? '',
      deviceTableId: d['deviceTableId'] ?? 0,
      deviceChangedCount: d['deviceChangedCount'] ?? 0,
      deviceChangedTime: d['deviceChangedTime'] ?? null,
      telefonNumarasi: d['telefonNumarasi'] ?? '',
      // 🔥 KVKK EKLENDİ
      kvkkApprovals: a,
    } as UserDetailModel;
  }
}
